import {
  ACCOUNT_MANAGEMENT_ITEMS,
  LESSON_ITEMS,
  PRESENTATION_ITEMS,
  STUDENT_STEPS,
} from "../guide/guideSteps";

/**
 * 그림으로 보는 이용 매뉴얼(/manual)과 학생용 안내문(/manual/student)의 글.
 * 장면 이미지는 tests/manual-shots가 실제 앱을 띄워 찍은 public/manual-scenes/<id>.webp다.
 * 장면 속 버튼 이름과 여기 글의 버튼 이름은 앱 문구와 글자 그대로 같아야 한다.
 */

export type ManualScene = {
  /** 이미지 파일 이름이자 앵커. */
  id: string;
  title: string;
  body: string;
  /** 화면 낭독기가 읽는 장면 설명. */
  alt: string;
};

export type ManualStep = {
  id: string;
  number: number;
  title: string;
  who: string;
  time: string;
  lead: string;
  scenes: ManualScene[];
};

export const MANUAL_STEPS: ManualStep[] = [
  {
    id: "step-1",
    number: 1,
    title: "학급 만들기",
    who: "선생님",
    time: "약 3분",
    lead: "학급 이름과 학생 번호만 정하면, 번호마다 학생 계정이 한꺼번에 만들어집니다.",
    scenes: [
      {
        id: "step-1-1",
        title: "「학급 만들기」를 누릅니다",
        body: "로그인하면 「내 학급」 화면이 열립니다. 처음에는 「학생과 함께 쓰는 방법」 안내가 먼저 뜨니 「확인했어요」로 닫고, 오른쪽 위의 「학급 만들기」를 누르세요.",
        alt: "아직 학급이 없는 「내 학급」 화면. 오른쪽 위 「학급 만들기」 버튼이 강조되어 있습니다.",
      },
      {
        id: "step-1-2",
        title: "세 칸을 채웁니다",
        body: "① 학급 이름, ② 계정 접두어(학생 아이디의 앞부분), ③ 학생 마지막 번호를 적고 아래 「학급 만들기」를 누릅니다. 계정 접두어는 만든 뒤에 바꿀 수 없어요.",
        alt: "학급 만들기 창. 학급 이름 3학년 1반, 계정 접두어 class3-1, 학생 마지막 번호 24가 입력되어 있고 세 칸에 1·2·3 번호가 붙어 있습니다.",
      },
      {
        id: "step-1-3",
        title: "학급이 만들어졌습니다",
        body: "학급 화면이 바로 열립니다. 미리 연습해 볼 수 있는 예시 캔버스가 하나 들어 있어요.",
        alt: "새로 만든 3학년 1반 학급 화면. 학급 이름과 예시 캔버스 카드가 강조되어 있습니다.",
      },
      {
        id: "step-1-4",
        title: "학생 계정을 나눠 줍니다",
        body: "「학생 계정」 탭에 번호별 아이디가 준비되어 있습니다. 「학생별 안내문 인쇄」로 아이마다 한 장씩 나눠 주세요. 반 전체가 나오는 「명단 인쇄」·「CSV 내려받기」는 선생님 보관용입니다. 학생 이름은 학생이 처음 로그인할 때 직접 적습니다.",
        alt: "학생 계정 탭. class3-1_1부터 번호별 아이디가 표로 늘어서 있고, 아이디 열과 「학생별 안내문 인쇄」 버튼이 강조되어 있습니다.",
      },
    ],
  },
  {
    id: "step-2",
    number: 2,
    title: "캔버스 만들기",
    who: "선생님",
    time: "약 1분",
    lead: "수업할 캔버스를 만들면 학생 번호마다 그릴 칸이 준비됩니다.",
    scenes: [
      {
        id: "step-2-1",
        title: "「캔버스 만들기」를 누릅니다",
        body: "학급 화면의 「캔버스」 탭에서 「캔버스 만들기」를 누르세요.",
        alt: "학급 화면의 캔버스 탭. 오른쪽 「캔버스 만들기」 버튼이 강조되어 있습니다.",
      },
      {
        id: "step-2-2",
        title: "제목을 적고 템플릿을 고릅니다",
        body: "① 캔버스 제목을 적고 ② 템플릿을 고른 뒤 「캔버스 만들기」를 누릅니다. 개인형은 학생마다 한 칸, 모둠형은 모둠마다 한 칸, 일반은 칸 없는 빈 캔버스입니다.",
        alt: "캔버스 만들기 창. 제목 칸에 우리 동네 지도가 입력되어 있고, 개인형·모둠형·일반 템플릿 중 개인형이 선택되어 있습니다.",
      },
      {
        id: "step-2-3",
        title: "캔버스가 생겼습니다",
        body: "목록에 새 캔버스가 보입니다. 같은 학급 학생은 모두 이 캔버스를 열 수 있어요.",
        alt: "캔버스 탭 목록에 우리 동네 지도 캔버스 카드가 새로 생겨 강조되어 있습니다.",
      },
      {
        id: "step-2-4",
        title: "번호마다 칸이 준비됩니다",
        body: "캔버스를 열면 1번부터 마지막 번호까지 칸이 나란히 있습니다. 이제 학생들을 들여보낼 차례예요.",
        alt: "선생님이 연 개인형 캔버스. 1번부터 24번까지 이름표가 붙은 빈 칸이 격자로 놓여 있습니다.",
      },
    ],
  },
  {
    id: "step-3",
    number: 3,
    title: "학생이 들어와 그리기",
    who: "학생",
    time: "약 2분",
    lead: "학생은 받은 아이디로 로그인해, 자기 번호 칸에 그림을 그립니다.",
    scenes: [
      {
        id: "step-3-1",
        title: "아이디와 비밀번호로 로그인합니다",
        body: "로그인 화면에서 ① 선생님께 받은 아이디와 ② 비밀번호(처음 비밀번호는 학생별 안내문에 적혀 있습니다)를 넣고 「로그인」을 누릅니다.",
        alt: "크루바스 로그인 화면. 아이디 칸에 class3-1_5, 비밀번호 칸에 비밀번호가 입력되어 있고 두 칸에 1·2 번호가 붙어 있습니다.",
      },
      {
        id: "step-3-2",
        title: "처음 한 번, 이름을 적습니다",
        body: "선생님이 알아볼 수 있게 ① 자기 이름을 적고 ② 「저장」을 누릅니다. 다음부터는 묻지 않아요.",
        alt: "「이름을 알려 주세요」 화면. 이름 칸에 이서준이 입력되어 있고 이름 칸과 저장 버튼이 강조되어 있습니다.",
      },
      {
        id: "step-3-3",
        title: "캔버스를 고릅니다",
        body: "선생님이 만든 캔버스가 보입니다. 오늘 쓸 캔버스를 눌러 엽니다.",
        alt: "학생의 학급 화면. 「열고 싶은 캔버스를 골라 주세요.」 안내 아래 우리 동네 지도 캔버스 카드가 강조되어 있습니다.",
      },
      {
        id: "step-3-4",
        title: "내 번호 칸이 보입니다",
        body: "캔버스를 열면 ① 내 번호 칸으로 바로 이동합니다. ② 위쪽 도구 막대에서 펜이나 도형을 골라 칸 안에 그립니다. 화면이 다른 곳으로 가 버렸다면 아래 「내 프레임 찾아가기」를 누르세요.",
        alt: "5번 학생이 연 캔버스. 가운데 5번 칸과 위쪽 도구 막대에 1·2 번호가 붙어 강조되어 있습니다.",
      },
      {
        id: "step-3-5",
        title: "내 칸에 그립니다",
        body: "그린 것은 따로 저장하지 않아도 저절로 저장됩니다.",
        alt: "5번 칸 안에 집과 나무가 그려진 학생 화면. 5번 칸이 강조되어 있습니다.",
      },
      {
        id: "step-3-6",
        title: "선생님 화면에서는 모두가 보입니다",
        body: "반 친구들이 각자 칸에 그리는 모습이 선생님 화면에 실시간으로 나타납니다.",
        alt: "선생님 화면. 1번부터 9번까지 칸에 학생들이 그린 그림이 채워지고 있고, 이름표가 달린 학생 커서가 보입니다.",
      },
    ],
  },
];

/** 자주 막히는 곳. 답은 사용 가이드의 문장을 그대로 쓴다. */
export const MANUAL_HELP = [
  { q: "학생이 비밀번호를 잊었어요.", a: ACCOUNT_MANAGEMENT_ITEMS[0] },
  { q: "학생이 이름을 잘못 적었어요.", a: ACCOUNT_MANAGEMENT_ITEMS[1] },
  {
    q: "학생이 들어왔는지 어떻게 알 수 있나요?",
    a: ACCOUNT_MANAGEMENT_ITEMS[3],
  },
  { q: "쓰지 않는 번호가 있어요.", a: ACCOUNT_MANAGEMENT_ITEMS[2] },
  { q: "휴대폰으로도 그릴 수 있나요?", a: LESSON_ITEMS[3] },
  { q: "학생마다 발표 자료를 만들게 하고 싶어요.", a: PRESENTATION_ITEMS[0] },
  { q: "학생 발표를 TV로 보여 주려면요?", a: PRESENTATION_ITEMS[3] },
];

/** 학생용 한 장 안내문의 네 칸. 이미지는 ③단계 장면과 그 클로즈업(-detail)이다. */
export const HANDOUT_STEPS = [
  {
    image: "step-3-1-detail",
    title: "로그인",
    body: "아래 적힌 아이디와 비밀번호를 넣고 「로그인」을 눌러요.",
  },
  {
    image: "step-3-2-detail",
    title: "이름 적기",
    body: "처음 한 번만 내 이름을 적고 「저장」을 눌러요.",
  },
  {
    image: "step-3-3-detail",
    title: "캔버스 고르기",
    body: "선생님이 알려 준 캔버스를 눌러요.",
  },
  {
    image: "step-3-4",
    title: "내 칸에 그리기",
    body: "내 번호 칸에 그려요. 그린 것은 저절로 저장돼요.",
  },
] as const;

/** 안내문 아래 약속. 학생 가이드 문장 중 막히기 쉬운 것만. */
export const HANDOUT_NOTES = [STUDENT_STEPS[4], STUDENT_STEPS[5]];

export const sceneSrc = (id: string) => `/manual-scenes/${id}.webp`;
